import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  Check,
  CheckCircle2,
  Crown,
  Loader2,
  Sparkles,
  XCircle,
} from "lucide-react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";

const getPlanActionLabel = ({ plan, subscriptionData, userRole }) => {
  if (
    subscriptionData?.subscriptionStatus === "Active" &&
    subscriptionData?.currentPlan
  ) {
    if (subscriptionData.planId ? subscriptionData.planId === plan.planID : subscriptionData.currentPlan === plan.plan) {
      return subscriptionData.cancelAtPeriodEnd ? "Renewal cancelled" : userRole !== 7 ? "Cancel plan" : "Current plan";
    }

    return "Change plan";
  }

  return "Buy plan";
};

const getPlanTone = (planName) => {
  if (planName === "Premium") {
    return {
      panel: "border-fuchsia-200 bg-fuchsia-50",
      icon: "bg-fuchsia-100 text-fuchsia-800",
      badge: "bg-fuchsia-800 text-white",
      accent: "bg-fuchsia-700",
    };
  }

  if (planName === "Starter") {
    return {
      panel: "border-purple-200 bg-purple-50",
      icon: "bg-purple-100 text-purple-700",
      badge: "bg-purple-100 text-purple-800",
      accent: "bg-purple-500",
    };
  }

  return {
    panel: "border-slate-200 bg-white",
    icon: "bg-slate-100 text-slate-700",
    badge: "bg-slate-100 text-slate-700",
    accent: "bg-slate-500",
  };
};

export default function BillingPricing({
  onAllSubscriptionData,
  onCurrentSubscriptionData,
  plans,
}) {
  const navigate = useNavigate();

  const [subscriptionData, setSubscriptionData] = useState(null);
  const [error, setError] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [openingCheckout, setOpeningCheckout] = useState(false);
  const checkoutPending = useRef(false);

  useEffect(() => {
    fetch("/api/current-subscription")
      .then((response) => {
        if (!response.ok) {
          throw new Error("Unable to load current subscription");
        }
        return response.json();
      })
      .then((data) => {
        setSubscriptionData(data);
        onCurrentSubscriptionData?.(data);
        onAllSubscriptionData(data.allSubscriptions || []);
        setUserRole(data.userRole);
      })
      .catch((fetchError) => {
        setError(fetchError.message);
        onAllSubscriptionData([]);
      });
  }, [onAllSubscriptionData, onCurrentSubscriptionData]);

  const handleCancelSubscription = (event) => {
    event.stopPropagation();

    confirmAlert({
      title: "Confirm to proceed",
      message: `Cancel renewal? Your plan stays available until ${new Date(subscriptionData.subscriptionEndDate).toLocaleDateString()}. You will not be charged for the next billing cycle.`,
      buttons: [
        {
          label: "Yes",
          onClick: () => {
            fetch("/api/cancel-subscription", {
              method: "DELETE",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({}),
            })
              .then((response) => {
                if (!response.ok) {
                  throw new Error("Failed to cancel subscription");
                }
                return response.json();
              })
              .then((data) => {
                setSubscriptionData(data);
                setUserRole(data.userRole);
                onCurrentSubscriptionData?.(data);
                onAllSubscriptionData(data.allSubscriptions || []);
              })
              .catch((cancelError) => {
                setError(cancelError.message);
              });
          },
        },
        {
          label: "No",
          onClick: () => {},
        },
      ],
    });
  };

  const handleUpdateSubscription = (event, plan) => {
    event.stopPropagation();
    if (checkoutPending.current) return;
    confirmAlert({
      title: `Change to ${plan.plan}?`,
      message: `You will be taken to secure payment checkout. After payment succeeds, your current ${subscriptionData.currentPlan} plan will be cancelled immediately and its remaining access will end. Your new plan starts immediately and renews automatically. Unused time does not carry over.`,
      buttons: [
        {
          label: "Continue to payment",
          onClick: async () => {
            if (checkoutPending.current) return;
            checkoutPending.current = true;
            setOpeningCheckout(true);
            setError(null);
            try {
              const response = await fetch("/api/update-subscription", {
                method: "PATCH", credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ planId: plan.planID, confirmReplace: true }),
              });
              const data = await response.json();
              if (!response.ok) throw new Error(data.error || "Unable to open checkout. Please try again.");
              if (data.status === "completed") { window.location.assign("/billing"); return; }
              if (!data.checkoutUrl) throw new Error(data.status === "pending"
                ? "Your earlier payment is still being confirmed. Please do not pay again; refresh billing shortly."
                : "That checkout has expired. Select Change plan to try again.");
              const checkoutUrl = new URL(data.checkoutUrl);
              if (checkoutUrl.protocol !== "https:" || checkoutUrl.hostname !== "checkout.stripe.com") throw new Error("Invalid checkout link. Please try again.");
              window.location.assign(checkoutUrl.href);
            } catch (checkoutError) {
              setError(checkoutError.message);
              checkoutPending.current = false;
              setOpeningCheckout(false);
            }
          },
        },
        { label: "Keep current plan", onClick: () => {} },
      ],
    });
  };

  const handleBuyPlan = (event, plan) => {
    event.stopPropagation();
    navigate("/billingcheckout", { state: { plan } });
  };

  if (error && !subscriptionData) {
    return (
      <div className="flex items-start gap-3 rounded-md border border-red-100 bg-red-50 p-4 text-sm text-red-700">
        <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
        <span>{error}</span>
      </div>
    );
  }

  if (!subscriptionData) {
    return (
      <div className="flex min-h-[220px] items-center justify-center rounded-md border border-slate-200 bg-slate-50 p-6 text-sm font-semibold text-gray-500">
        <Loader2 className="mr-2 h-4 w-4 animate-spin text-purple-700" />
        Loading subscription details
      </div>
    );
  }

  if (!plans.length) {
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 p-6 text-center">
        <Sparkles className="mx-auto h-6 w-6 text-purple-700" aria-hidden="true" />
        <p className="mt-3 text-sm font-semibold text-gray-700">
          No plans available right now.
        </p>
      </div>
    );
  }

  return (
    <>
      {error && <div role="alert" className="mb-4 rounded-md border border-red-100 bg-red-50 p-4 text-sm text-red-700">{error}</div>}
      {openingCheckout && <p role="status" className="mb-4 text-sm font-semibold text-purple-700">Opening secure payment checkout...</p>}
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      {plans.map((plan) => {
        const isCurrentPlan = subscriptionData.planId ? subscriptionData.planId === plan.planID : subscriptionData.currentPlan === plan.plan;
        const actionLabel = getPlanActionLabel({
          plan,
          subscriptionData,
          userRole,
        });
        const tone = getPlanTone(plan.plan);
        const isSelected = selectedPlan?.plan === plan.plan;

        return (
          <article
            key={plan.planID || plan.plan}
            className={`relative flex min-h-[420px] cursor-pointer flex-col overflow-hidden rounded-md border p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${
              tone.panel
            } ${isCurrentPlan ? "ring-2 ring-fuchsia-700 ring-offset-2" : ""}`}
            onClick={() => setSelectedPlan(plan)}
            aria-label={`${plan.plan} subscription plan`}
          >
            <div className={`absolute inset-x-0 top-0 h-1 ${tone.accent}`} />

            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`inline-flex rounded-md px-2.5 py-1 text-xs font-bold uppercase ${tone.badge}`}
                  >
                    {plan.plan === "Premium" ? "Best value" : "Subscription"}
                  </span>
                  {isCurrentPlan && (
                    <span className="inline-flex items-center gap-1 rounded-md bg-white px-2.5 py-1 text-xs font-bold uppercase text-fuchsia-800 ring-1 ring-fuchsia-100">
                      <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                      Your plan
                    </span>
                  )}
                </div>
                <h3 className="mt-4 text-2xl font-extrabold text-gray-900">
                  {plan.plan}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {plan.additionalDetails}
                </p>
              </div>

              <span
                className={`grid h-11 w-11 shrink-0 place-items-center rounded-md ${tone.icon}`}
              >
                <Crown className="h-5 w-5" aria-hidden="true" />
              </span>
            </div>

            <div className="mt-6 rounded-md bg-white/80 p-4 shadow-sm ring-1 ring-black/5">
              <div className="flex items-end gap-2">
                <span className="text-4xl font-extrabold text-gray-900">
                  ${plan.price}
                </span>
                <span className="pb-1 text-sm font-semibold text-gray-500">
                  /month
                </span>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
                <span className="rounded-md bg-slate-100 px-2.5 py-1 font-bold text-gray-700">
                  {plan.duration} month plan
                </span>
                <span className="rounded-md bg-green-50 px-2.5 py-1 font-bold text-green-700 ring-1 ring-green-100">
                  {plan.savings === "No Savings"
                    ? "No savings"
                    : `Save ${plan.savings}%`}
                </span>
                <span className="text-gray-400 line-through">
                  {plan.cancelledMRP}
                </span>
              </div>
            </div>

            <ul className="mt-5 space-y-3">
              {plan.features.map((feature, index) => (
                <li key={`${plan.plan}-${index}`} className="flex gap-2 text-sm text-gray-700">
                  <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-md bg-white text-purple-700 shadow-sm">
                    <Check className="h-3.5 w-3.5" aria-hidden="true" />
                  </span>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <div className="mt-auto pt-6">
              {subscriptionData.subscriptionStatus === "Active" &&
              subscriptionData.currentPlan ? (
                isCurrentPlan ? (
                  userRole !== 7 ? (
                    <button
                      type="button"
                      className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-md bg-white px-4 py-2.5 text-sm font-bold text-red-700 shadow-sm ring-1 ring-red-100 transition hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-300 focus:ring-offset-2"
                      disabled={subscriptionData.cancelAtPeriodEnd || openingCheckout}
                      onClick={handleCancelSubscription}
                    >
                      <XCircle className="h-4 w-4" aria-hidden="true" />
                      {actionLabel}
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled
                      className="inline-flex min-h-11 w-full cursor-not-allowed items-center justify-center gap-2 rounded-md bg-slate-100 px-4 py-2.5 text-sm font-bold text-gray-500"
                    >
                      <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                      {actionLabel}
                    </button>
                  )
                ) : (
                  <button
                    type="button"
                    className="inline-flex min-h-11 w-full items-center justify-center rounded-md bg-violet-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2"
                    disabled={openingCheckout}
                    onClick={(event) => handleUpdateSubscription(event, plan)}
                  >
                    {actionLabel}
                  </button>
                )
              ) : (
                <button
                  type="button"
                  className="inline-flex min-h-11 w-full items-center justify-center rounded-md bg-violet-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2"
                  onClick={(event) => handleBuyPlan(event, plan)}
                >
                  {actionLabel}
                </button>
              )}

              {isCurrentPlan && subscriptionData.cancelAtPeriodEnd && (
                <p className="mt-3 text-center text-xs font-semibold text-purple-700">
                  Access until {new Date(subscriptionData.subscriptionEndDate).toLocaleDateString()}. No further renewal.
                </p>
              )}
              {isSelected && !isCurrentPlan && (
                <p className="mt-2 text-center text-xs font-semibold text-purple-700">
                  Selected for review
                </p>
              )}
            </div>
          </article>
        );
      })}
    </div>
    </>
  );
}
